import { CheckCircleIcon, InfoIcon, XIcon, XCircleIcon, type Icon } from "@phosphor-icons/react";
import { createContext, useCallback, useContext, useState, type ReactNode, useEffect, type CSSProperties } from "react";
import { Animated, Platform, Pressable, StyleSheet, View, useWindowDimensions } from "react-native";
import { createPortal } from "react-dom";
import { v4 as uuidv4 } from "uuid";

import CustomText from "../../components/common/general/CustomText/CustomText";
import CustomButton from "../../components/common/general/CustomButton/CustomButton";
import { spacing } from "../../styles/design-system";

interface ToastProperties {
  message: string;
  type: "info" | "error" | "success" | "warning";
  visibleDuration?: number;
  actions?: {
    actionText: string;
    actionCallback: () => void;
  }[];
}

interface ToastContextType {
  showToast: (properties: ToastProperties) => {
    id: string;
    close: () => void;
  };
  closeToast: (id: string) => void;
}

type ToastItem = {
  id: string;
  close: () => void;
  isClosing: boolean;
  isVisible: boolean;
  anim: Animated.Value; // Controls entry/exit
  stackAnim: Animated.Value; // Controls dynamic vertical stacking (0 -> -10, -20, etc.)
} & ToastProperties;

const ToastContext = createContext<ToastContextType | null>(null);
const WEB_TOAST_PORTAL_STYLE: CSSProperties = {
  position: "fixed",
  inset: 0,
  zIndex: 99999,
  pointerEvents: "none",
  overflow: "hidden",
};
const TOAST_BOTTOM_OFFSET = 96;

export const ToastProvider = ({ children }: { children: ReactNode }) => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const { width: viewportWidth } = useWindowDimensions();

  const ANIMATION_DURATION = 300;
  const SLIDE_OFFSET = 60;
  const STACK_SPACING = 10;
  const STACK_ANIM_DURATION = 250;
  const toastWidth = Math.max(280, Math.min(460, viewportWidth - spacing.lg * 2));

  const animateIn = useCallback((anim: Animated.Value) => {
    Animated.timing(anim, {
      toValue: 0,
      duration: ANIMATION_DURATION,
      useNativeDriver: true,
    }).start();
  }, []);

  const animateOut = useCallback((anim: Animated.Value, callback: () => void) => {
    Animated.timing(anim, {
      toValue: SLIDE_OFFSET,
      duration: ANIMATION_DURATION,
      useNativeDriver: true,
    }).start(callback);
  }, []);

  const closeToast = useCallback(
    (id: string) => {
      setToasts((prev) => {
        const toastToClose = prev.find((t) => t.id === id);

        if (toastToClose) {
          animateOut(toastToClose.anim, () => {
            setToasts((current) => current.filter((toast) => toast.id !== id));
          });

          return prev.map((toast) => (toast.id === id ? { ...toast, isClosing: true } : toast));
        }
        return prev;
      });
    },
    [animateOut]
  );

  const showToast = useCallback(
    (properties: ToastProperties) => {
      const id = uuidv4();
      const anim = new Animated.Value(SLIDE_OFFSET);
      const stackAnim = new Animated.Value(0);

      const close = () => {
        closeToast(id);
      };

      const newToast: ToastItem = {
        id,
        close,
        isClosing: false,
        isVisible: true,
        anim,
        stackAnim,
        ...properties,
      };

      setToasts((prev) => [...prev, newToast]);
      animateIn(anim);

      if (properties.visibleDuration !== 0) {
        setTimeout(close, properties.visibleDuration ?? 5000);
      }

      return { close, id };
    },
    [closeToast, animateIn]
  );

  useEffect(() => {
    const totalToasts = toasts.length;

    toasts.forEach((toast, index) => {
      const targetOffset = (totalToasts - 1 - index) * STACK_SPACING * -1;

      Animated.timing(toast.stackAnim, {
        toValue: targetOffset,
        duration: STACK_ANIM_DURATION,
        useNativeDriver: true,
      }).start();
    });
  }, [toasts, STACK_SPACING]);

  const renderToastItem = (toast: ToastItem, index: number) => {
    const combinedTranslateY = Animated.add(toast.anim, toast.stackAnim);

    const opacity = toast.anim.interpolate({
      inputRange: [0.5, SLIDE_OFFSET],
      outputRange: [1, 0],
    });

    const baseBottom = 0;

    return (
      <Animated.View
        pointerEvents="auto"
        key={toast.id}
        style={[
          styles.toastItem,
          styles[toast.type],
          {
            bottom: baseBottom,
            width: toastWidth,
            transform: [{ translateY: combinedTranslateY }],
            opacity: opacity,
            zIndex: index,
          },
        ]}>
        {/* Toast Content */}
        <View style={styles.toastIcon}>
          {toast.type === "info" && <InfoIcon color="#3B82F6" size={20} weight="fill" />}
          {toast.type === "error" && <XCircleIcon color="#EF4444" size={20} weight="fill" />}
          {toast.type === "success" && <CheckCircleIcon color="#22C55E" size={20} weight="fill" />}
          {toast.type === "warning" && <InfoIcon color="#F59E42" size={20} weight="fill" />}
        </View>
        <View style={styles.toastMessage}>
          <CustomText text={toast.message} useTranslationText={false} style={styles.messageContent} />
        </View>
        {toast.actions?.length > 0 && (
          <View style={styles.actionsWrapper}>
            {toast.actions?.map((action) => (
              <CustomButton
                key={`${Math.random()}-${action.actionText}`}
                styleType="primary"
                size="s"
                variant="text"
                text={action.actionText}
                onPress={action.actionCallback}
              />
            ))}
          </View>
        )}
        <View style={styles.closeToastBtn}>
          <Pressable
            style={styles.closeIconBtn}
            onPress={() => {
              closeToast(toast.id);
            }}>
            <XIcon />
          </Pressable>
        </View>
      </Animated.View>
    );
  };

  const toastLayer = (
    <View pointerEvents="box-none" style={styles.toastViewport}>
      <View pointerEvents="box-none" style={styles.toastContainer}>
        {toasts.map(renderToastItem)}
      </View>
    </View>
  );

  return (
    <ToastContext.Provider value={{ showToast, closeToast }}>
      {children}
      {Platform.OS === "web" && typeof document !== "undefined"
        ? createPortal(<div style={WEB_TOAST_PORTAL_STYLE}>{toastLayer}</div>, document.body)
        : toastLayer}
    </ToastContext.Provider>
  );
};

const styles = StyleSheet.create({
  toastViewport: {
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    overflow: "hidden",
    zIndex: 99999,
  },
  toastContainer: {
    position: "absolute",
    zIndex: 10000,
    bottom: TOAST_BOTTOM_OFFSET,
    right: spacing.lg,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  },
  toastItem: {
    position: "absolute",
    right: 0,
    paddingTop: 18,
    paddingRight: 24,
    paddingBottom: 18,
    paddingLeft: 20,
    maxWidth: 500,
    minHeight: 70,
    borderRadius: 14,
    borderWidth: 1.5,
    borderColor: "#F4F4F5",
    flexDirection: "row",
    gap: 16,
    alignItems: "center",
    padding: 12,
    marginVertical: 4,
    backgroundColor: "#fff",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 5,
    zIndex: 99999,
  },
  toastIcon: {},
  toastMessage: { flex: 1 },
  messageContent: { fontSize: 16, color: "#232129", fontWeight: 500 },
  actionsWrapper: {},
  closeToastBtn: {},
  closeIconBtn: { padding: 4, borderRadius: 100 },
});

export const useToast = () => {
  const context = useContext(ToastContext);
  return context;
};
